/**
 * Every failure the client can produce, as one closed set.
 *
 * The distinction that matters to a screen is **transport versus domain**:
 *
 * - A `NetworkError`, `TimeoutError` or `CancelledError` means the request
 *   never got an answer. Retrying is reasonable; the user's data is untouched.
 * - An `ApiResponseError` means the server answered and said no. Retrying the
 *   same request will fail the same way; the screen has to show why.
 *
 * Conflating them is how an app tells a user "check your connection" when the
 * server actually said their password was wrong.
 */
export class ApiClientError extends Error {
}
/** The request never reached the server, or the connection failed. */
export class NetworkError extends ApiClientError {
    kind = 'network';
    retryable = true;
    constructor(cause) {
        super('The request could not reach the server.');
        this.name = 'NetworkError';
        this.cause = cause;
    }
}
/** The request was abandoned after the client's deadline. */
export class TimeoutError extends ApiClientError {
    timeoutMs;
    kind = 'timeout';
    retryable = true;
    constructor(timeoutMs) {
        super(`The request did not complete within ${String(timeoutMs)}ms.`);
        this.timeoutMs = timeoutMs;
        this.name = 'TimeoutError';
    }
}
/**
 * The caller aborted the request.
 *
 * Distinct from a timeout because a screen must **not** show an error for it:
 * the usual cause is the user navigating away, and "something went wrong" on a
 * screen they already left is a bug, not a diagnosis.
 */
export class CancelledError extends ApiClientError {
    kind = 'cancelled';
    retryable = false;
    constructor() {
        super('The request was cancelled.');
        this.name = 'CancelledError';
    }
}
/**
 * The server answered with a non-2xx status and the documented error envelope.
 */
export class ApiResponseError extends ApiClientError {
    status;
    code;
    details;
    requestId;
    kind = 'response';
    constructor(status, code, message, details, requestId) {
        super(message);
        this.status = status;
        this.code = code;
        this.details = details;
        this.requestId = requestId;
        this.name = 'ApiResponseError';
    }
    /** A 5xx may be transient; a 4xx is the client's own request. */
    get retryable() {
        return this.status >= 500 || this.code === 'RATE_LIMITED';
    }
    get isValidation() {
        return this.code === 'VALIDATION_FAILED';
    }
    get isUnauthenticated() {
        return this.code === 'UNAUTHENTICATED';
    }
    get isNotFound() {
        return this.code === 'NOT_FOUND';
    }
    /** Field errors for a form, or an empty object. */
    fieldErrors() {
        return this.details ?? {};
    }
    /** The first message for a field, which is what a form field shows. */
    firstErrorFor(field) {
        return this.details?.[field]?.[0];
    }
}
/**
 * The server answered, but not with something this contract describes.
 *
 * Raised rather than guessed at. A proxy returning an HTML error page, or a
 * gateway truncating a body, must not be reported as a successful empty
 * response — **the client does not invent server success.**
 */
export class MalformedResponseError extends ApiClientError {
    status;
    bodyPreview;
    kind = 'malformed';
    retryable = false;
    constructor(status, reason, bodyPreview) {
        super(`The server returned a response the client could not read: ${reason}`);
        this.status = status;
        this.bodyPreview = bodyPreview;
        this.name = 'MalformedResponseError';
    }
}
export function isApiClientError(error) {
    return error instanceof ApiClientError;
}
export function isCancelled(error) {
    return error instanceof CancelledError;
}
//# sourceMappingURL=errors.js.map